import { FC, useEffect, useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/ui/select';
import { Currency } from '@/constants';
import { MempoolFees } from '@/networks/btc-like/common';
import { useBtcLikeMempoolFees } from '@/networks/btc-like/hooks';
import { cn } from '@/utils';
import Big from '@/utils/Bigjs';

import { UtxoSelectModalInfoFeeRateInput } from './UtxoSelectModalInfoFeeRateInput';

export type FeeType = keyof MempoolFees | 'custom';

export const UtxoSelectModalInfoSelectFee: FC<{
  vbyteEstimate: number | undefined;
  onChangeVbyteEstimate: (vbyteEstimate: number | undefined) => void;
}> = ({ vbyteEstimate, onChangeVbyteEstimate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: mempoolFees } = useBtcLikeMempoolFees(Currency.BTC);
  const [selectedFeeType, setSelectedFeeType] = useState<FeeType>('economyFee');

  const handleFeeChange = (value: FeeType) => {
    setSelectedFeeType(value);
    if (value === 'custom') return;
    onChangeVbyteEstimate(mempoolFees?.[value] || undefined);
  };

  useEffect(() => {
    if (selectedFeeType !== 'custom') {
      onChangeVbyteEstimate(mempoolFees?.[selectedFeeType] || undefined);
    }
  }, [mempoolFees]);

  return (
    <div className="flex w-full items-center gap-2">
      <Select open={isOpen} onOpenChange={setIsOpen} defaultValue="economyFee" onValueChange={handleFeeChange}>
        {selectedFeeType === 'custom' ? (
          <div className="hover:bg-background/80 bg-background/60 relative flex items-center gap-2 rounded-lg">
            <SelectTrigger className="rtl absolute left-0 border-0 bg-transparent" />
            <UtxoSelectModalInfoFeeRateInput
              className="z-10"
              vbyteEstimate={vbyteEstimate}
              onChangeVbyteEstimate={onChangeVbyteEstimate}
            />
          </div>
        ) : (
          <SelectTrigger className="hover:bg-background/80 bg-background/60 h-8 rounded-lg border-0">
            <SelectValue placeholder="Mempool Fee">{vbyteEstimate?.toFixed(2)} sat/vB</SelectValue>
          </SelectTrigger>
        )}

        <SelectContent className="flex flex-col gap-2">
          <SelectItem
            disabled
            value="minimumFee"
            className={cn(selectedFeeType === 'minimumFee' && 'bg-foreground/40')}
          >
            <div className="flex flex-col items-start gap-0">
              <span>{Big(mempoolFees?.minimumFee ?? 0).toFixed(2)} sat/vB</span>
              <span className="text-muted-foreground max-w-40 text-xs whitespace-normal">
                Smallest fee in upcoming mempool block
              </span>
            </div>
          </SelectItem>
          <SelectItem value="economyFee" className={cn(selectedFeeType === 'economyFee' && 'bg-foreground/40')}>
            <div className="flex flex-col items-start gap-0">
              <span>{Big(mempoolFees?.economyFee ?? 0).toFixed(2)} sat/vB</span>
              <span className="text-muted-foreground max-w-40 text-xs whitespace-normal">
                Economy fee, lower inclusion chance in next block
              </span>
            </div>
          </SelectItem>
          <SelectItem value="fastestFee" className={cn(selectedFeeType === 'fastestFee' && 'bg-foreground/40')}>
            <div className="flex flex-col items-start gap-0">
              <span>{Big(mempoolFees?.fastestFee ?? 0).toFixed(2)} sat/vB</span>
              <span className="text-muted-foreground max-w-40 text-xs whitespace-normal">
                Fastest fee, likely to be included in next block
              </span>
            </div>
          </SelectItem>
          <SelectItem value="custom" className={cn(selectedFeeType === 'custom' && 'bg-foreground/40')}>
            <div className="flex flex-col items-start gap-0">
              <span>Custom Fee</span>
              <span className="text-muted-foreground max-w-40 text-xs whitespace-normal">
                Set the fee rate manually
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
